import { useState } from "react";
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { digitsOnly, phoneInputProps } from "@/lib/numberInput";
import { Building2, Loader2, Mail, MessageSquare, Phone, Send, User, X } from "lucide-react";
import { Analytics } from "@/lib/analyticsShim";

function FieldShell({ id, label, required = false, icon: Icon, multiline = false, children }) {
  const iconClass = multiline
    ? "pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-gray-400 transition group-focus-within:text-[#C8A96A]"
    : "pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 transition group-focus-within:text-[#C8A96A]";

  return (
    <div className="group rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition focus-within:border-[#C8A96A] focus-within:shadow-[0_0_0_4px_rgba(200,169,106,0.18)]">
      <Label htmlFor={id} className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#4B5563]">
        {label}{required ? " *" : ""}
      </Label>
      <div className="relative mt-2">
        <Icon className={iconClass} />
        {children}
      </div>
    </div>
  );
}

export default function ContactModal({ open, onOpenChange }) {
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    phone: "",
    email: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const inputClass =
    "h-11 w-full rounded-md border border-gray-300 bg-[#FAFAFA] px-3 py-2 pl-10 text-sm text-[#111827] outline-none transition placeholder:text-gray-400 focus:border-[#C8A96A] focus:bg-white focus:ring-2 focus:ring-[#C8A96A]/20";
  const textareaClass =
    "min-h-28 w-full resize-y rounded-md border border-gray-300 bg-[#FAFAFA] px-3 py-2.5 pl-10 text-sm text-[#111827] outline-none transition placeholder:text-gray-400 focus:border-[#C8A96A] focus:bg-white focus:ring-2 focus:ring-[#C8A96A]/20";

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ company_name: "", contact_name: "", phone: "", email: "", message: "" });
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        variant="drawer"
        className="flex flex-col overflow-y-auto border-l border-gray-200"
        data-testid="contact-modal"
      >
        <div className="sticky top-0 z-10 border-b border-gray-100 bg-white px-5 py-4">
          <DialogClose
            type="button"
            className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-[#4B5563] transition hover:border-[#C8A96A] hover:text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#C8A96A]/30"
            aria-label="Close contact form"
          >
            <X className="h-4 w-4" />
          </DialogClose>
          <DialogHeader className="pr-12">
            <DialogTitle className="font-display text-2xl text-[#111827]">Get in Touch</DialogTitle>
            <DialogDescription>
              Share your details and our team will reach out within 24 hours.
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={submit} className="flex-1 space-y-4 bg-gray-50/70 px-5 py-5">
          <FieldShell id="company_name" label="Company Name" icon={Building2} required>
            <Input
              id="company_name"
              required
              autoFocus
              placeholder="Enter company name"
              value={form.company_name}
              onChange={(e) => setForm({ ...form, company_name: e.target.value })}
              className={inputClass}
              data-testid="contact-company-input"
            />
          </FieldShell>

          <FieldShell id="contact_name" label="Contact Name" icon={User} required>
            <Input
              id="contact_name"
              required
              placeholder="Enter contact name"
              value={form.contact_name}
              onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
              className={inputClass}
              data-testid="contact-name-input"
            />
          </FieldShell>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FieldShell id="phone" label="Phone" icon={Phone} required>
              <Input
                id="phone"
                required
                placeholder="10 digit number"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: digitsOnly(e.target.value, 10) })}
                {...phoneInputProps}
                className={inputClass}
                data-testid="contact-phone-input"
              />
            </FieldShell>

            <FieldShell id="email" label="Email" icon={Mail} required>
              <Input
                id="email"
                type="email"
                required
                placeholder="name@company.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputClass}
                data-testid="contact-email-input"
              />
            </FieldShell>
          </div>

          <FieldShell id="message" label="Message" icon={MessageSquare} multiline>
            <Textarea
              id="message"
              rows={3}
              placeholder="Tell us what you need"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className={textareaClass}
              data-testid="contact-message-input"
            />
          </FieldShell>

          <button
            type="submit"
            disabled={submitting}
            className="btn-gold flex w-full items-center justify-center gap-2 rounded-md py-3 text-sm font-semibold shadow-sm disabled:opacity-60"
            data-testid="contact-submit-btn"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send Message
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
